import { Fragment, useState } from "react";
import { api } from "../api";
import { usePoll, ErrorBanner, Loading, ScopeSelect, scopeQuery, LANES, LaneBadge, StatusBadge, SimBadge, ExecBadges, Modal, OPEN_STATUSES, isOpenBet, f2, f3, signed, when, polyLink, useModeSettings, useToast } from "./common";

const TABS = [
  { key: "open", label: "En cours", status: OPEN_STATUSES.join(",") },
  { key: "settled", label: "Clôturés", status: "won,lost,exited,void" },
  { key: "rejected", label: "Rejetés", status: "rejected" },
  { key: "pending", label: "À valider", status: "proposed,pending_validation,approved" },
];
const PERIODS = [["1", "24 h"], ["7", "7 jours"], ["30", "30 jours"], ["", "Tout"]];

function Metric({ label, children, cls }) {
  return <div className="m"><div className="lab">{label}</div><div className={"v " + (cls || "")}>{children}</div></div>;
}

// Ligne dépliée : tout ce qui explique le pari, sans encombrer le tableau.
function Detail({ b }) {
  const open = isOpenBet(b);
  const exitReason = b.rejectionReason && /^Sortie/.test(b.rejectionReason) ? b.rejectionReason : null;
  return (
    <div>
      <div className="detail-grid">
        <Metric label="Mise">{f2(b.stake)} $ · {Number(b.shares || 0).toFixed(2)} parts</Metric>
        <Metric label="Prix d'entrée">{f3(b.marketPrice)}{b.entryAsk != null && <span className="muted"> (ask {f3(b.entryAsk)})</span>}</Metric>
        {open && b.currentPrice != null && <Metric label="Prix actuel">{f3(b.currentPrice)}</Metric>}
        {b.exitPrice != null && <Metric label="Prix de sortie">{f3(b.exitPrice)}</Metric>}
        {b.lane !== "copy" && <Metric label="Edge à l'entrée" cls={(b.edgePct || 0) >= 0 ? "up" : "down"}>{signed(b.edgePct, 1, " %")}{open && b.currentEdgePct != null && <span className={b.currentEdgePct >= 0 ? "up" : "down"}> → {signed(b.currentEdgePct, 1, " %")}</span>}</Metric>}
        {b.lane !== "copy" && b.estProb != null && <Metric label="Proba estimée">{(b.estProb * 100).toFixed(0)} %{b.reEstProb != null && ` → ${(b.reEstProb * 100).toFixed(0)} %`}</Metric>}
        {b.confidence != null && <Metric label="Confiance">{(b.confidence * 100).toFixed(0)} %</Metric>}
        <Metric label="CLV" cls={b.clvCents == null ? "muted" : b.clvCents >= 0 ? "up" : "down"}>{b.clvCents == null ? "—" : signed(b.clvCents, 1, "c")}</Metric>
        {b.daysToResolution != null && <Metric label="Échéance">{b.daysToResolution} j</Metric>}
        {b.feePaid > 0 && <Metric label="Frais">{Number(b.feePaid).toFixed(3)} $</Metric>}
        <Metric label="Placé le">{when(b.placedAt || b.createdAt)}</Metric>
        {b.settledAt && <Metric label="Clôturé le">{when(b.settledAt)}</Metric>}
      </div>
      {exitReason && <div className="rationale"><strong>Clôture :</strong> {exitReason.replace(/^Sortie : /, "")}</div>}
      {b.status === "rejected" && b.rejectionReason && <div className="rationale down"><strong>Rejet :</strong> {b.rejectionReason}</div>}
      {b.rationale && <div className="rationale"><strong>Pourquoi :</strong> {b.rationale}</div>}
      {b.riskNotes && <div className="muted small" style={{ marginBottom: 8 }}>Exécution : {b.riskNotes}</div>}
      {b.research && <details className="fold" style={{ marginBottom: 8 }}><summary>Recherche</summary><div className="prose" style={{ marginTop: 6 }}>{b.research}</div></details>}
      <div className="row center" style={{ gap: 10 }}>
        <ExecBadges bet={b} />
        <a href={polyLink(b)} target="_blank" rel="noreferrer" className="small">voir sur polymarket.com ↗</a>
      </div>
    </div>
  );
}

function PendingCard({ bet, onDone, settings }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const toast = useToast();
  const liveArmed = settings?.mode === "live" && !!settings?.live_trading_armed && bet.simulated === false;
  async function act(kind) {
    setBusy(true);
    try { await api(`/bets/${bet._id}/${kind}`, { method: "POST", body: {} }); setConfirm(false); toast(kind === "approve" ? "Pari validé." : "Pari refusé."); onDone(); }
    catch (e) { toast(e.message, "down"); } finally { setBusy(false); }
  }
  const canAct = ["proposed", "pending_validation"].includes(bet.status);
  return (
    <div className="card pending-card">
      <div className="row-between"><span className="row center" style={{ gap: 6 }}><LaneBadge lane={bet.lane} /><SimBadge bet={bet} /></span><StatusBadge status={bet.status} /></div>
      <div className="q">{bet.question} <span className="outcome">· {bet.outcome}</span></div>
      <div className="prob-bar" title={`Estimé ${(bet.estProb * 100).toFixed(0)} % vs marché ${(bet.marketPrice * 100).toFixed(0)} %`}><i style={{ width: (bet.estProb || 0) * 100 + "%" }} /></div>
      <div className="detail-grid" style={{ marginBottom: 0 }}>
        <Metric label="Marché">{(bet.marketPrice * 100).toFixed(0)} %</Metric>
        <Metric label="Estimé" cls="up">{(bet.estProb * 100).toFixed(0)} %</Metric>
        <Metric label="Edge" cls={(bet.edgePct || 0) >= 0 ? "up" : "down"}>{signed(bet.edgePct, 1, " %")}</Metric>
        <Metric label="Mise">{f2(bet.stake)} $</Metric>
        <Metric label="Échéance">{bet.daysToResolution ? bet.daysToResolution + " j" : "—"}</Metric>
      </div>
      {bet.rationale && <div className="rationale" style={{ marginBottom: 0 }}>{bet.rationale}</div>}
      {canAct && (
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn-success btn-sm grow" disabled={busy} onClick={() => (liveArmed ? setConfirm(true) : act("approve"))}>{liveArmed ? "Valider et miser (réel)" : "Valider et miser"}</button>
          <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => act("reject")}>Refuser</button>
        </div>
      )}
      {bet.status === "approved" && <div className="notice notice-warn small">Signal validé, mode réel non armé : à placer à la main sur Polymarket ({f2(bet.stake)} $ @ {f3(bet.marketPrice)}).</div>}
      {confirm && (
        <Modal title="Placer un ordre réel ?" onCancel={() => setConfirm(false)} onConfirm={() => act("approve")} confirmLabel="Oui, miser en réel" busy={busy} danger>
          <p>Le trading réel est armé : valider envoie un ordre sur Polymarket avec de l'argent réel.</p>
          <p className="mono">{f2(bet.stake)} $ sur « {bet.outcome} » à {f3(bet.marketPrice)}</p>
          <p className="muted small" style={{ marginBottom: 0 }}>{bet.question}</p>
        </Modal>
      )}
    </div>
  );
}

export default function Bets() {
  const [tab, setTab] = useState("open");
  const [lane, setLane] = useState("");
  const [days, setDays] = useState("7");
  const [scope, setScope] = useState("");
  const [openRow, setOpenRow] = useState(null);
  const settings = useModeSettings();
  const t = TABS.find((x) => x.key === tab);
  const periodic = tab === "settled" || tab === "rejected";
  const q = `/bets?status=${t.status}${lane ? `&lane=${lane}` : ""}${periodic && days ? `&days=${days}` : ""}${scopeQuery(scope)}&limit=500`;
  const { data, err, refresh } = usePoll(() => api(q), 15000, [q]);
  const { data: pend } = usePoll(() => api(`/bets?status=proposed,pending_validation,approved${scopeQuery(scope)}&limit=50`), 20000, [scope]);
  const nPending = pend?.bets?.length || 0;

  const rows = data?.bets || [];
  const totStake = rows.reduce((a, b) => a + (Number(b.stake) || 0), 0);
  const totPnl = rows.reduce((a, b) => a + (Number(isOpenBet(b) ? b.unrealizedPnl : b.pnl) || 0), 0);
  const won = rows.filter((b) => !isOpenBet(b) && (b.pnl || 0) > 0).length;

  return (
    <div>
      <div className="page-head">
        <div><h1>Paris</h1><div className="sub">Positions ouvertes, historique et décisions — cliquez une ligne pour le détail.</div></div>
        <div className="page-actions"><ScopeSelect scope={scope} onChange={setScope} current={settings?.mode} /></div>
      </div>
      <ErrorBanner err={err} />

      <div className="tabs">
        {TABS.filter((x) => x.key !== "pending" || nPending > 0 || settings?.autonomy !== "auto").map((x) => (
          <button key={x.key} className={"tab" + (tab === x.key ? " active" : "")} onClick={() => { setTab(x.key); setOpenRow(null); }}>
            {x.label}{x.key === "pending" && nPending > 0 && <span className="n">{nPending}</span>}{x.key === tab && data && <span className="n">{rows.length}</span>}
          </button>
        ))}
      </div>

      {tab === "pending" ? (
        !pend ? <Loading /> : nPending === 0 ? <div className="card table-empty">Rien à valider : en autonomie automatique, les paris sont placés sans validation.</div> : (
          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
            {pend.bets.map((b) => <PendingCard key={b._id} bet={b} onDone={refresh} settings={settings} />)}
          </div>
        )
      ) : (
        <>
          <div className="filters">
            <div className="chips">{LANES.map((l) => <button key={l.key} className={"chip" + (lane === l.key ? " active" : "")} onClick={() => setLane(l.key)}>{l.label}</button>)}</div>
            {periodic && <div className="chips">{PERIODS.map(([k, l]) => <button key={k} className={"chip" + (days === k ? " active" : "")} onClick={() => setDays(k)}>{l}</button>)}</div>}
          </div>
          {!data && !err ? <Loading /> : (
            <div className="card table-wrap">
              <table className="table">
                <thead><tr>
                  <th>Quand</th><th>Voie</th><th>Marché</th><th className="right">Mise</th><th className="right">Prix</th><th className="right">P&L</th><th>Statut</th>
                </tr></thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={7} className="table-empty">Aucun pari ici{periodic && days ? " sur la période" : ""}.</td></tr>}
                  {rows.map((b) => {
                    const open = isOpenBet(b);
                    const pnl = open ? b.unrealizedPnl : b.pnl;
                    const expanded = openRow === b._id;
                    return (
                      <Fragment key={b._id}>
                        <tr className="clickable" onClick={() => setOpenRow(expanded ? null : b._id)}>
                          <td className="muted tiny nowrap">{when(tab === "settled" ? (b.settledAt || b.placedAt) : (b.placedAt || b.createdAt))}</td>
                          <td><LaneBadge lane={b.lane} /></td>
                          <td style={{ maxWidth: 420 }}><span className="q"><SimBadge bet={b} /> {b.question} <span className="outcome">· {b.outcome}</span></span>{b.fillPending || b.pendingSell || b.redeemPending ? <div style={{ marginTop: 3 }}><ExecBadges bet={b} /></div> : null}</td>
                          <td className="right mono">{f2(b.stake)}</td>
                          <td className="right mono nowrap">{f3(b.marketPrice)}{open && b.currentPrice != null ? <span className="muted"> → {f3(b.currentPrice)}</span> : b.exitPrice != null ? <span className="muted"> → {f3(b.exitPrice)}</span> : ""}</td>
                          <td className={"right mono nowrap " + (pnl == null ? "muted" : pnl >= 0 ? "up" : "down")}>{pnl == null ? "—" : signed(pnl)}{open && pnl != null ? <span className="muted tiny"> lat.</span> : ""}</td>
                          <td><StatusBadge status={b.status} /></td>
                        </tr>
                        {expanded && <tr className="detail"><td colSpan={7}><Detail b={b} /></td></tr>}
                      </Fragment>
                    );
                  })}
                </tbody>
                {rows.length > 1 && (
                  <tfoot><tr>
                    <td colSpan={3} className="muted">{rows.length} paris{!periodic || tab === "settled" ? ` · ${won} gagnant${won > 1 ? "s" : ""}` : ""}</td>
                    <td className="right mono">{f2(totStake)}</td><td />
                    <td className={"right mono " + (totPnl >= 0 ? "up" : "down")}>{signed(totPnl)}</td><td />
                  </tr></tfoot>
                )}
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
